/**
 * sessionAccess.js — who a session is for, who started it, and who may delete it.
 *
 * Clinician-run sessions (egdl_Core CLINICIAN-SESSIONS-PLAN.md): a clinician can start a
 * session for a patient linked to them (the session is the patient's, `started_by` names
 * the clinician) or for a guest (the clinician's own, marked `guest`, with an optional
 * `guest_label`). A clinician's GET /api/sessions includes their own guest sessions.
 *
 * Deleting (DELETE /api/sessions/:id): a clinician may delete any session they can see; a
 * patient only their own, and only while no clinician has a verified link to them. The
 * portal decides; this only says when to offer it. A deleted session disappears from every
 * list at once and its data is kept, so an administrator can still restore it.
 */
import { useCallback, useState } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { useLinkedPlayers } from './api/useLinkedPlayers';
import { isExperience, serverErrorText } from './accountFacts';
import { Button, useToast } from './components/ui';
import './sessionAccess.css';

const same = (a, b) => a != null && b != null && String(a) === String(b);

/** "Guest", "Guest · Open day visitor", or the linked player's name. */
export function playerLabel(session, nameOf) {
    if (session?.guest) return session.guest_label ? `Guest · ${session.guest_label}` : 'Guest';
    return nameOf(session?.user_id) || 'Unknown player';
}

/** "You", the clinician's name, "A clinician", or null for a session the player started. */
export function startedByName(session, user, nameOf) {
    if (!session?.started_by) return null;
    if (same(session.started_by, user?.user_id)) return 'You';
    return nameOf(session.started_by) || 'A clinician';
}

/** "Started by you" / "Started by Dr Kersul" under a session's game; null for a guest or a player's own. */
export function startedByLabel(session, user, nameOf) {
    if (session?.guest) return null; // the player column already says Guest
    const who = startedByName(session, user, nameOf);
    if (!who) return null;
    return `Started by ${who === 'You' || who === 'A clinician' ? who.toLowerCase() : who}`;
}

/** A game cell with its "Started by …" line. */
export function GameCell({ game, origin }) {
    if (!origin) return game;
    return (
        <span className="sa-game">
            <span className="sa-game__name">{game}</span>
            <span className="sa-game__origin">{origin}</span>
        </span>
    );
}

/** canDelete(session): whether to offer Delete to the signed-in account. */
export function useSessionAccess() {
    const { user } = useAuth();
    const { providers, loading, error } = useLinkedPlayers();
    // until the links are known (or if they fail to load) a patient is not offered Delete
    const linkedClinician = loading || !!error || providers.some((p) => p.verified);
    const canDelete = useCallback((session) => {
        if (!user || !session) return false;
        // an experience account stores nothing on the portal, deleting included (403 local_only)
        if (isExperience(user)) return false;
        if (user.role === 'provider') return true;
        return user.role === 'player' && same(session.user_id, user.user_id) && !linkedClinician;
    }, [user, linkedClinician]);
    return { canDelete };
}

/**
 * Delete with an inline confirmation (the redesign's unlink pattern):
 *   const del = useDeleteSession(session, onDeleted);
 *   <Button … onClick={del.ask}>Delete</Button> … <DeleteConfirm del={del} />
 */
export function useDeleteSession(session, onDeleted) {
    const { authFetch } = useAuth();
    const toast = useToast();
    const [asking, setAsking] = useState(false);
    const [busy, setBusy] = useState(false);

    const remove = async () => {
        setBusy(true);
        try {
            const res = await authFetch(`${API_BASE}/api/sessions/${encodeURIComponent(session._id)}`, { method: 'DELETE' });
            if (!res.ok) {
                let message = '';
                try { message = serverErrorText(await res.json()); } catch { /* not JSON */ }
                throw new Error(message || `The session wasn’t deleted (${res.status}).`);
            }
            setAsking(false);
            toast('Session deleted.', 'success');
            onDeleted?.(session._id);
        } catch (e) {
            toast(e.message || 'The session wasn’t deleted.', 'error');
        } finally {
            setBusy(false);
        }
    };

    return { session, asking, busy, ask: () => setAsking((a) => !a), cancel: () => setAsking(false), remove };
}

export function DeleteConfirm({ del }) {
    if (!del.asking) return null;
    const what = del.session?.guest
        ? 'This guest session disappears from your sessions.'
        : 'This session disappears for the patient and for every clinician linked to them.';
    return (
        <div className="sa-confirm" role="group" aria-label="Confirm deleting this session">
            <span className="sa-confirm__text">Delete this session? {what} An administrator can still restore it.</span>
            <span className="sa-confirm__btns">
                <Button variant="danger" disabled={del.busy} onClick={del.remove}>{del.busy ? 'Deleting…' : 'Delete session'}</Button>
                <Button variant="ghost" muted disabled={del.busy} onClick={del.cancel}>Cancel</Button>
            </span>
        </div>
    );
}
